import { useEffect, useState, useRef, useCallback } from "react";
import {
	usePrinterConfig,
	usePdfProcessor,
	useEpsonPrinter,
} from "@plevands/epson-thermal-printer";

import { Button } from "@/components/atoms/button/button";
import { Modal } from "@/components/organisms/modal/modal";
import { useAuth } from "@/store/use-auth";

interface PdfViewerProps {
	fileUrl: string;
	showModal: boolean;
	fileName?: string;
	setShowModal: (showModal: boolean) => void;
	shouldPrint?: boolean;
}

export function PdfViewer(props: PdfViewerProps) {
	const { fileUrl, showModal, fileName, setShowModal, shouldPrint } = props;
	const { token } = useAuth();
	const [blobUrl, setBlobUrl] = useState<string | null>(null);
	const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
	const hasProcessedRef = useRef(false);
	const currentFileUrlRef = useRef<string>("");
	const pdfProcessor = usePdfProcessor();
	const { config } = usePrinterConfig();
	const { printPages } = useEpsonPrinter(config);

	// Guardar referencias estables de pdfProcessor y printPages
	const pdfProcessorRef = useRef(pdfProcessor);
	const printPagesRef = useRef(printPages);

	useEffect(() => {
		pdfProcessorRef.current = pdfProcessor;
		printPagesRef.current = printPages;
	}, [pdfProcessor, printPages]);

	// Efecto para cargar el PDF
	useEffect(() => {
		if (!fileUrl || !token) return;

		// Si es el mismo archivo que ya procesamos, no hacer nada
		if (currentFileUrlRef.current === fileUrl) return;

		// Resetear el flag de procesamiento cuando cambia el archivo
		hasProcessedRef.current = false;

		if (fileUrl.includes("data:application/pdf;")) {
			setBlobUrl(fileUrl);
			currentFileUrlRef.current = fileUrl;

			// Convertir data URL a Blob para que el botón de imprimir funcione
			fetch(fileUrl)
				.then((res) => res.blob())
				.then((blob) => setPdfBlob(blob))
				.catch((error) =>
					console.error("Error al convertir data URL a blob:", error),
				);
			return;
		}

		const fetchPdf = async () => {
			try {
				const response = await fetch(
					fileUrl + "&filename=" + encodeURIComponent(fileName || ""),
					{ headers: { Authorization: `Bearer ${token}` } },
				);
				const blob = await response.blob();
				setBlobUrl(URL.createObjectURL(blob));
				setPdfBlob(blob);
				currentFileUrlRef.current = fileUrl;
			} catch (error) {
				console.error("Error al cargar PDF:", error);
			}
		};

		fetchPdf();
	}, [fileUrl, token, fileName]);

	const processPdfAndPrint = useCallback(async () => {
		if (!pdfBlob) return;

		const file = new File([pdfBlob], fileName || "document.pdf", {
			type: "application/pdf",
		});
		const [{ canvas }] = await pdfProcessorRef.current.processFile(file);
		await printPagesRef.current([canvas]);
	}, [pdfBlob, fileName]);

	// Efecto separado para imprimir (solo una vez)
	useEffect(() => {
		// Si shouldPrint es false, resetear el flag
		if (!shouldPrint) {
			hasProcessedRef.current = false;
			return;
		}

		if (!pdfBlob || hasProcessedRef.current) return;

		const printPdf = async () => {
			hasProcessedRef.current = true;

			try {
				await processPdfAndPrint();
			} catch (error) {
				console.error("Error al imprimir PDF:", error);
			}
		};

		printPdf();
	}, [shouldPrint, pdfBlob, processPdfAndPrint]);

	const handlePrint = async () => {
		try {
			await processPdfAndPrint();
		} catch (error) {
			console.error("Error al imprimir PDF:", error);
		}
	};

	return (
		<Modal show={showModal}>
			<div className="modal-body">
				<div
					className="overflow-scroll"
					style={{ height: "75vh", maxHeight: "75vh" }}
				>
					{blobUrl && (
						<iframe
							src={blobUrl}
							style={{ width: "100%", height: "100%", border: 0 }}
						/>
					)}
				</div>
			</div>
			<div className="modal-footer">
				<Button mode="primary" onClick={handlePrint} disabled={!pdfBlob}>
					Imprimir
				</Button>
				<Button mode="secondary" onClick={() => setShowModal(false)}>
					Cerrar
				</Button>
			</div>
		</Modal>
	);
}
